/*
 * varify-order-stamp.js
 * ---------------------------------------------------------------------------
 * Stamps the visitor's Varify experiment/variation assignment onto the Shopify
 * cart (as cart attributes) so every resulting ORDER is attributable to its
 * A/B arm server-side — independent of Varify's own (consent-gated) pixel.
 *
 * WHY: Varify reads conversions from GA4 (suppressed by cookie consent) and
 * Shopify side-by-side, but nothing variation-specific is written to the order,
 * so test trials can't be separated from organic/standalone-page trials, and
 * the two flows contaminate each other. This closes that gap.
 *
 * SOURCE OF TRUTH (consent-independent): Varify persists each bucket in
 *   localStorage["varify-experiment-<experimentId>"] = {"variationId":<id|"Original">,"timestamp":...}
 * and mirrors it at window.varify.appliedExperiments[<id>].variationId. Both are
 * set when the variant is APPLIED (functional), not when it's TRACKED, so they
 * exist even without analytics consent. Checkout is a different origin, so we
 * capture on the storefront and write into the cart before the customer leaves.
 *
 * RESULT: order.customAttributes gets one key per active experiment, e.g.
 *   varify_37943 = "56891"   varify_37942 = "56889"
 * Auto-discovers every active experiment, so future tests are covered with no
 * code change. Reads back via the Admin API order.customAttributes field.
 */
(function () {
  'use strict';

  var ATTR_PREFIX = 'varify_'; // -> cart attribute keys like "varify_37943"
  var CART_URLS = /\/cart\/(add|change|clear)(\.js)?(\?|$)/; // triggers a re-stamp
  var applying = false;
  var pending = false;

  // ---- read the visitor's current Varify assignments (expId -> variationId) ----
  function getAssignments() {
    var out = {};
    // 1) Persisted buckets in localStorage (survive navigation; set w/o consent).
    try {
      for (var i = 0; i < window.localStorage.length; i++) {
        var key = window.localStorage.key(i);
        if (!key || key.indexOf('varify-experiment-') !== 0) continue;
        if (key.indexOf('exclusion') !== -1) continue; // skip exclusion flags
        var expId = key.slice('varify-experiment-'.length);
        if (!expId) continue;
        var raw = window.localStorage.getItem(key);
        var variationId = raw;
        try {
          var parsed = JSON.parse(raw);
          // Varify stores {variationId,timestamp}; variationId is null for the
          // unmodified Original/control arm — label that "Original" (and never
          // leak the raw JSON, whose per-visitor timestamp would fragment it).
          if (parsed && typeof parsed === 'object' && 'variationId' in parsed) {
            variationId = parsed.variationId == null ? 'Original' : parsed.variationId;
          }
        } catch (e) { /* non-JSON raw string value — keep as-is */ }
        if (variationId != null && variationId !== '') out[expId] = String(variationId);
      }
    } catch (e) { /* localStorage blocked */ }

    // 2) Runtime fallback (in-memory, definitely set on render regardless of consent).
    try {
      var applied = window.varify && window.varify.appliedExperiments;
      if (applied && typeof applied === 'object') {
        Object.keys(applied).forEach(function (expId) {
          var v = applied[expId] && applied[expId].variationId;
          if (v != null && out[expId] == null) out[expId] = String(v);
        });
      }
    } catch (e) { /* no runtime state */ }

    return out;
  }

  // ---- ensure the cart carries the current assignments (only if a cart exists) ----
  function apply() {
    if (applying) { pending = true; return; }
    var assignments = getAssignments();
    var expIds = Object.keys(assignments);
    if (!expIds.length) return; // nothing to stamp yet

    applying = true;
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.item_count) { return null; } // don't create an empty cart
        var existing = cart.attributes || {};
        var updates = {};
        expIds.forEach(function (expId) {
          var attrKey = ATTR_PREFIX + expId;
          if (String(existing[attrKey] || '') !== assignments[expId]) {
            updates[attrKey] = assignments[expId];
          }
        });
        if (!Object.keys(updates).length) return null; // already stamped & current
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: updates }),
        });
      })
      .catch(function () { /* fail-open: never block the cart */ })
      .then(function () {
        applying = false;
        if (pending) { pending = false; apply(); }
      });
  }

  var debounceTimer = null;
  function scheduleApply() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(apply, 250);
  }

  // ---- hook every add-to-cart path (fetch + XHR), site-wide ----
  function isCartMutation(url) {
    return typeof url === 'string' && CART_URLS.test(url) &&
      url.indexOf('/cart/update') === -1; // our own writes must not re-trigger
  }

  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var p = origFetch.apply(this, arguments);
      if (isCartMutation(url)) { p.then(function () { scheduleApply(); }, function () {}); }
      return p;
    };
  }

  var origOpen = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
  if (origOpen) {
    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.__varifyCartMutation = isCartMutation(url);
      return origOpen.apply(this, arguments);
    };
    var origSend = window.XMLHttpRequest.prototype.send;
    window.XMLHttpRequest.prototype.send = function () {
      if (this.__varifyCartMutation) {
        this.addEventListener('load', function () { scheduleApply(); });
      }
      return origSend.apply(this, arguments);
    };
  }

  // ---- initial pass: stamp an already-populated cart, after Varify applies ----
  function init() { scheduleApply(); }
  if (window.varify && window.varify.loaded) init();
  window.addEventListener('varify:loaded', init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
